import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Alert,
  Box,
  List,
  ListSubheader,
  Stack,
  ListItemButton,
} from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { Organization } from '@/types/organizations'
import {
  createOrganizationMap,
  getFilteredOrgsNodesData,
  getNodesToExpand,
  getOrgName,
} from '@/utils/organization'
import OrganizationSearchInput from '@/components/organizations/OrganizationSearchInput'
import { useRecentOrganizationsContext } from '@/components/organizations/RecentOrganizationsProvider'
import OrganizationTreeItem, {
  OrganizationTreeItemLabel,
} from '@/components/organizations-tree/OrganizationTreeItem'

const SEARCH_DEBOUNCE_MS = 250

type OrganizationTreeSearchProps = {
  organizations: Organization[]
  rootTreeOrgId: string
  selectedOrgId: string
  selectedOrganization: Organization
  setSelectedOrgId: React.Dispatch<React.SetStateAction<string>>
  allNodeIsIncluded?: boolean
  disabledOrgIds?: string[]
  showRecentOrgs?: boolean
}

export default function OrganizationTreeSearch({
  organizations,
  rootTreeOrgId,
  selectedOrgId,
  selectedOrganization,
  setSelectedOrgId,
  allNodeIsIncluded,
  disabledOrgIds,
  showRecentOrgs = true,
}: OrganizationTreeSearchProps) {
  const [recentOrgs] = useRecentOrganizationsContext()
  // The input keeps the raw value so typing stays instant; the tree filters off
  // the debounced copy, so a burst of keystrokes costs one filter pass.
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedSetSearchTerm = useMemo(
    () => debounce(setSearchTerm, SEARCH_DEBOUNCE_MS),
    [],
  )

  useEffect(() => {
    debouncedSetSearchTerm(inputValue)
  }, [debouncedSetSearchTerm, inputValue])

  // A pending timer firing after unmount would set state on a dead component.
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm],
  )

  const organizationMap = useMemo(
    () => createOrganizationMap(organizations),
    [organizations],
  )

  const defaultExpanded = useMemo(
    () =>
      selectedOrgId === rootTreeOrgId || selectedOrgId === 'multiple'
        ? [rootTreeOrgId]
        : getNodesToExpand(selectedOrgId, organizationMap),
    [selectedOrgId, organizationMap, rootTreeOrgId],
  )

  const [expanded, setExpanded] = useState(defaultExpanded)

  const { visibleOrgIds, nodesToExpand } = useMemo(
    () =>
      getFilteredOrgsNodesData({
        organizations,
        organizationMap,
        defaultExpanded,
        searchedValue: searchTerm,
      }),

    [defaultExpanded, searchTerm, organizationMap, organizations],
  )

  // Stable callbacks so memo(OrganizationTreeItem) can skip unchanged subtrees.
  const getIsVisible = useCallback(
    (id: string) => visibleOrgIds.has(id),
    [visibleOrgIds],
  )

  const getIsDisabled = useCallback(
    (id: string) => !!disabledOrgIds?.includes(id),
    [disabledOrgIds],
  )

  useEffect(() => {
    if (searchTerm) {
      setExpanded(nodesToExpand)
    }
  }, [searchTerm, nodesToExpand])

  return (
    <Stack direction="row" spacing={2} sx={{ minHeight: '300px' }}>
      <Stack sx={{ width: '300px' }} spacing={3}>
        {selectedOrgId && (
          <Box sx={{ pl: 1.75 }}>
            <OrganizationSearchInput
              autoFocus
              inputValue={inputValue}
              disableCloseOnSelect={false}
              organizations={organizations}
              selected={selectedOrganization}
              setSelected={(org) => {
                setSelectedOrgId(org.id)
                setExpanded(getNodesToExpand(org.id, organizationMap))
              }}
              setInputValue={setInputValue}
              getOptionDisabled={(option) =>
                !!disabledOrgIds?.includes(option.id)
              }
            />
          </Box>
        )}
        {showRecentOrgs && recentOrgs.length > 0 && (
          <List
            disablePadding
            subheader={
              <ListSubheader sx={{ lineHeight: 'initial', pb: 1 }}>
                Recent Organizations
              </ListSubheader>
            }
          >
            {recentOrgs.map((org) => (
              <ListItemButton
                key={org.id}
                disabled={!!disabledOrgIds?.includes(org.id)}
                onClick={() => {
                  setSelectedOrgId(org.id)
                  setExpanded(getNodesToExpand(org.id, organizationMap))
                }}
              >
                <OrganizationTreeItemLabel
                  code={org.code}
                  name={getOrgName(org)}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        {visibleOrgIds.size ? (
          <SimpleTreeView
            slots={{
              collapseIcon: ExpandMore,
              expandIcon: ChevronRight,
            }}
            expandedItems={
              !searchTerm && !expanded.length ? [rootTreeOrgId] : expanded
            }
            selectedItems={selectedOrgId}
            onSelectedItemsChange={(_e, id) => {
              if (!id || disabledOrgIds?.includes(id)) return
              setSelectedOrgId(id)
            }}
            onExpandedItemsChange={(_e, ids) => {
              setExpanded(ids)
            }}
          >
            {allNodeIsIncluded && visibleOrgIds.has('all') && (
              <TreeItem itemId="all" label="All Organizations" />
            )}
            <OrganizationTreeItem
              orgTree={organizationMap[rootTreeOrgId]}
              searchTerm={searchTerm}
              getIsVisible={getIsVisible}
              getIsDisabled={disabledOrgIds ? getIsDisabled : undefined}
            />
          </SimpleTreeView>
        ) : (
          <Alert severity="info">No results found.</Alert>
        )}
      </Box>
    </Stack>
  )
}
